/**
 * Shared cache-clearing and SW update utilities.
 *
 * Two distinct flows:
 * 1. activateUpdateAndReload() — for the update dialog. Activates the
 *    waiting SW via SKIP_WAITING so its precached assets are preserved,
 *    then reloads. This is the fast path.
 * 2. clearCachesAndReload() — for error recovery (chunk errors, preload
 *    errors). Nuclear option: wipes all caches, unregisters all SWs,
 *    then reloads so everything is fetched fresh.
 *
 * Both functions include a timeout fallback that force-reloads after
 * RELOAD_TIMEOUT_MS even if the async work stalls, so the user is
 * never stuck on an "Updating..." spinner.
 */

const RELOAD_TIMEOUT_MS = 8_000; // 8 seconds — generous for slow networks

/**
 * Schedules a forced reload as a safety net.
 * Returns the timer ID so callers can clear it if the happy path completes first.
 */
const scheduleForceReload = (): ReturnType<typeof setTimeout> =>
  setTimeout(() => {
    console.warn('[CacheUtils] Timeout reached — forcing reload');
    window.location.reload();
  }, RELOAD_TIMEOUT_MS);

/**
 * Clears all browser caches and unregisters all service workers.
 * Used by error-recovery paths where we need a completely fresh state.
 */
export const clearAllCaches = async (): Promise<void> => {
  try {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));

    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    console.log('[CacheUtils] Cleared all caches and unregistered service workers');
  } catch (error) {
    console.error('[CacheUtils] Failed to clear caches:', error);
  }
};

/**
 * Error-recovery path: clears all caches and force-reloads.
 * Includes timeout fallback — reload happens even if cache clearing stalls.
 */
export const clearCachesAndReload = async (): Promise<void> => {
  const fallback = scheduleForceReload();
  await clearAllCaches();
  clearTimeout(fallback);
  window.location.reload();
};

/**
 * Update path: activates the waiting service worker via SKIP_WAITING,
 * then reloads. Preserves the new SW's precached assets so the reload
 * is fast instead of re-downloading everything from scratch.
 * Includes timeout fallback — reload happens even if SW activation stalls.
 */
export const activateUpdateAndReload = async (): Promise<void> => {
  const fallback = scheduleForceReload();

  try {
    if ('serviceWorker' in navigator) {
      // Race against a 2s timeout — navigator.serviceWorker.ready never
      // resolves if no SW is registered (e.g. ?force-update on localhost).
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000)),
      ]);

      if (registration?.waiting) {
        // Tell the waiting SW to activate
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });

        // Wait for the waiting SW to transition state (installed → activating → activated).
        // Resolves on the first statechange event, or after 1s as a fallback
        // in case the event fires before we attach the listener.
        await new Promise<void>((resolve) => {
          registration.waiting?.addEventListener('statechange', () => resolve(), { once: true });
          setTimeout(resolve, 1000);
        });
      }
    }
  } catch (error) {
    console.error('[CacheUtils] Failed to activate waiting SW:', error);
  }

  clearTimeout(fallback);
  window.location.reload();
};
